import React, { useState, useEffect, useMemo } from 'react';
import { Box, Collapse, Button, TextInput, NumberInput, Textarea, useMantineTheme, createStyles, MultiSelect, Select, Grid, Badge } from '@mantine/core';
import { DataTable } from 'mantine-datatable';
import { IconPrinter, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { AppBar } from '@mui/material';
import NavBar from '../Navigation/navBar';
// Import JSON data
import brandsData from './brandsData.json'
import departmentsData from './departmentsData.json';
import classesData from './classesData.json';
interface SizeDetails {
  Size: string;
  Qty: number;
  Description: string;
  Brand: string;
  Department: string;
  Location: string;
  dcLocation: string;
  Sold_Qty_90: number;
  SoldQty_Last30Days: number;
}

interface ApiResponse {
  Style: string;
  DC_Caledonia: SizeDetails[];
  Steeles: SizeDetails[];
}

interface AggregatedData {
  id: string;
  Style: string;
  Description: string;
  InStock: number;
  DC_Qty: number;
  Brand: string;
  Sold_Qty_90: number;
  SoldQty_Last30Days: number;
  Department: string;
  sizes: {
    Size: string;
    StoreQty: number;
    DCQty: number;
    Description: string;
    Location: string;
    dcLocation: string;
  }[];
}

const useStyles = createStyles((theme) => ({
  root: {
    border: `1px solid ${theme.colors.dark[0]}`,
    borderRadius: theme.radius.md,
  },
  header: {
    fontStyle: 'italic',
    '&& th': { color: theme.colors.dark[8] },
  },
  footer: {
    fontStyle: 'italic',
    '&& th': { color: theme.colors.red[6] },
  },
  pagination: {
    color: theme.colors.dark[6],
    'button[data-active="true"]': {
      background: theme.colors.dark[4],
    },
    'button[data-active="true"]:not([data-disabled="true"]):hover': {
      background: theme.colors.dark[5],
    },
  },
  inputSection: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#F1F6F9',
    padding: theme.spacing.xs,
    borderTop: `1px solid ${theme.colors.gray[3]}`,
  },
  noBorder: {
    border: 'none',

  },
  dataTableContainer: {
    height: `calc(95vh - 130px)`, // Adjust based on the height of fixed AppBar and inputSection
    overflowY: 'auto',
    paddingBottom: theme.spacing.xl, // Extra padding to prevent overlap
  },
}));

const locationOptionsData = ['DC-Newmarket', 'DC-Richmond', 'DC-Aurora'];

const InventoryTable: React.FC = () => {
  const [data, setData] = useState<ApiResponse[]>([]);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [recordsPerPage] = useState(25);
  const [locationOptions, setLocationOptions] = useState<string>('DC-Richmond'); // Default to 'DC' or whichever default you prefer

  //const [notes, setNotes] = useState<Record<string, string>>({});
  function getInitialStartDate(): string {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  }
  const { classes } = useStyles();
  const [startDate, setStartDate] = useState<string>(getInitialStartDate());
  
  const [brand, setBrand] = useState<string[]>(['FILA']);
  const [department, setDepartment] = useState<string[]>(['Mens Footwear']);
  const [productClass, setProductClass] = useState<string[]>([]);
  const [styles, setStyles] = useState<string[]>([]);
  const [separatedStyleCodes, setSeparatedStyleCodes] = useState<string[]>([]);
  const [qtyLess, setQtyLess] = useState<number>(3);
  const [qtyGreater, setQtyGreater] = useState<number>(20);
  const [currentInput, setCurrentInput] = useState<string>('');

  //const [open, setOpen] = useState<boolean>(false);
  const theme = useMantineTheme();
  const [sizeRequests, setSizeRequests] = useState<Record<string, Record<string, { quantity: number; notes: string }>>>({});
  // const handleClose = () => {
  //   // Reset the states to their initial values upon closing the dialog
  //   setStartDate(getInitialStartDate());
  //   setOpen(false);
  // };
  // const formatDate = (date: Date) => {
  //   return date.toISOString().split('T')[0];
  // };

  //const endDate = formatDate(new Date());
  const fetchData = async () => {
    try {
      const response = await fetch('/fetch-restock-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ brand, department, class: productClass, qtyLess, qtyGreater, locationOptions, styles }),
      });

      if (!response.ok) {
        console.error('Failed to fetch data:', response.statusText);
        return;
      }

      const result: ApiResponse[] = await response.json();
      console.log('Fetched data:', result); // Log the fetched data for debugging

      // Check if data is as expected, if not, log an error and early return
      if (!Array.isArray(result)) {
        console.error('Unexpected data format received:', result);
        return;
      }

      setData(result); // Correctly set the data if format matches
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  // Ensure that we get aggregated data whenever `data` changes
  const aggregatedData: AggregatedData[] = useMemo(() => {
    return data.map((item) => {
      const totalDCQty = item.DC_Caledonia.reduce((sum, entry) => sum + entry.Qty, 0);
      const totalSteelesQty = item.Steeles.reduce((sum, entry) => sum + entry.Qty, 0);
      const totalSoldQty90Days = item.Steeles.reduce((sum, entry) => sum + entry.Sold_Qty_90, 0);
      const totalSoldQty30Days = item.Steeles.reduce((sum, entry) => sum + entry.SoldQty_Last30Days, 0);

      const sizes = item.DC_Caledonia.map((dcEntry) => {
        const steelesEntry = item.Steeles.find((s) => s.Size === dcEntry.Size);
        return {
          Size: dcEntry.Size,
          StoreQty: steelesEntry?.Qty || 0,
          DCQty: dcEntry.Qty,
          Description: dcEntry.Description,
          Brand: dcEntry.Brand,
          Sold_Qty_90: steelesEntry?.Sold_Qty_90 || 0,
          SoldQty_Last30Days: steelesEntry?.SoldQty_Last30Days || 0,
          Location: steelesEntry?.Location || 'N/A',
          dcLocation: dcEntry.dcLocation || 'N/A',
          Style: item.Style
        };
      });

      return {
        id: item.Style,
        Style: item.Style,
        Description: item.DC_Caledonia[0]?.Description || '',
        InStock: totalSteelesQty,
        DC_Qty: totalDCQty,
        Sold_Qty_90: totalSoldQty90Days,
        SoldQty_Last30Days: totalSoldQty30Days,
        Brand: item.DC_Caledonia[0]?.Brand || '',
        Department: item.DC_Caledonia[0]?.Department || '',
        sizes,
      };
    });
  }, [data]); // Dependency on `data` to ensure re-computation

  useEffect(() => {
    console.log('Aggregated data:', aggregatedData); // Log the aggregated data for debugging
  }, [aggregatedData]);


  const toggleRow = (style: string) => {
    setExpandedRow(expandedRow === style ? null : style);
  };


  const handleSizeRequestChange = (style: string, size: string, field: 'quantity' | 'notes', value: number | string) => {
    setSizeRequests((prev) => ({
      ...prev,
      [style]: {
        ...prev[style],
        [size]: {
          ...prev[style]?.[size],
          [field]: value,
        },
      },
    }));
  };

  const handleStyleCodesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentInput(e.target.value);
  };
  
  // Handler for committing the input when Enter is pressed or input loses focus
  const handleCommitStyles = () => {
    const splitStyles = currentInput.split(',').map((code) => code.trim()).filter((code) => code !== '');
    setStyles(splitStyles);
    setSeparatedStyleCodes(splitStyles);
  };
  const handleRequestSubmit = async (style: string) => {
    // Retrieve the row data for the specific style
    const rowData = aggregatedData.find((item) => item.Style === style);
  
    // Guard clause if rowData is not found, log an error and exit
    if (!rowData) {
      console.error('Style not found in aggregated data:', style);
      alert('Failed to submit restock request. Style not found.');
      return;
    }
  
    // Prepare size requests data from user inputs
    const sizeRequestsData = Object.entries(sizeRequests[style] || {}).map(([size, request]) => ({
      size,
      requested_quantity: request.quantity,
      notes: request.notes || null,
    }));
  
    // Create request payload including brand and department from rowData
    const requestPayload = {
      style_no: style,
      dclocation: locationOptions,
      department: rowData.Department,  // Extract department from rowData
      brand: rowData.Brand,            // Extract brand from rowData
      sizeRequests: sizeRequestsData,
    };
  
    console.log("Request Payload:", requestPayload); // Log the payload for verification
  
    try {
      const response = await fetch('/submit-restock-request', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestPayload),
      });
  
      const result = await response.json();
      if (response.ok) {
        alert(result.message);
      } else {
        alert(result.message || 'Failed to submit restock request');
      }
    } catch (error) {
      console.error('Error submitting restock request:', error);
    }
  };
  



  const handlePrint = async (styleCode: string) => {
    // Open a new window for the report display
    const reportWindow = window.open('', '_blank');
    if (!reportWindow) {
      alert('Failed to open new window. Please check your popup blocker settings.');
      return;
    }
    reportWindow.document.write('Loading report...'); // Immediate feedback for the user

    try {
      // Create a POST request to fetch the PDF
      const response = await fetch('/report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ styleCode, startDate }),
      });

      if (response.ok) {
        // Create a blob from the response data
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        // Set the new window's location to the blob URL to display the PDF
        reportWindow.location.href = url;
      } else {
        reportWindow.close(); // Close the new window if the fetch fails
        alert('Failed to generate report');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      reportWindow.close(); // Ensure to close the new window on error
      alert('An error occurred while generating the report.');
    }
  };


  // Handler for the style codes input, splitting by commas
  // const handleStyleCodesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const value = e.target.value;
  //   const splitStyles = value.split(',').map((code) => code.trim()).filter((code) => code !== '');
  //   setStyles(splitStyles); // Set styles directly as an array
  //   setSeparatedStyleCodes(splitStyles);
  // };

  return (
    <>
      <AppBar position='sticky'>
        <NavBar />
      </AppBar>

      <Box className={classes.dataTableContainer}>
        <DataTable
          withBorder
          withColumnBorders
          records={aggregatedData.slice((page - 1) * recordsPerPage, page * recordsPerPage)}
          columns={[
            {
              accessor: 'expand',
              title: '',
              width: 25,
              render: (record) => (
                <Button variant="subtle" compact onClick={() => toggleRow(record.Style)}>
                  {expandedRow === record.Style ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                </Button>
              ),
              titleStyle: { backgroundColor: '#212A3E', color: '#F1F6F9' }
            },
            { accessor: 'Style', title: 'Style No', width: 230, titleStyle: { backgroundColor: '#212A3E', color: '#F1F6F9' } },
            { accessor: 'Description', title: 'Description', width: 250, titleStyle: { backgroundColor: '#212A3E', color: '#F1F6F9' } },
            { accessor: 'InStock', title: 'InStock', width: 70, titleStyle: { backgroundColor: '#212A3E', color: '#F1F6F9' } },
            { accessor: 'DC_Qty', title: 'DC Qty', width: 70, titleStyle: { backgroundColor: '#212A3E', color: '#F1F6F9' } },
            { accessor: 'Sold_Qty_90', title: 'Sold_Qty_90', width: 90, titleStyle: { backgroundColor: '#212A3E', color: '#F1F6F9' } },
            { accessor: 'SoldQty_Last30Days', title: 'Sold_Qty_30', width: 90, titleStyle: { backgroundColor: '#212A3E', color: '#F1F6F9' } },
            { accessor: 'Brand', title: 'Brand', width: 85, titleStyle: { backgroundColor: '#212A3E', color: '#F1F6F9' } },
            { accessor: 'Department', title: 'Department', width: 100, titleStyle: { backgroundColor: '#212A3E', color: '#F1F6F9' } },

            {
              accessor: 'Print',
              title: 'StyleRead',
              width: 60,
              render: (record) => (
                <Button variant="subtle" color="gray" size="xs" onClick={() => handlePrint(record.Style)}>
                  <IconPrinter size={18} />
                </Button>
              ),
              titleStyle: { backgroundColor: '#212A3E', color: '#F1F6F9' }
            },
          ]}
          rowExpansion={{
            allowMultiple: false,
            content: ({ record }) => (
              <Collapse in={expandedRow === record.Style}>
                <Box sx={{ padding: 10, backgroundColor: '#f3f4f6' }}>
                  <DataTable
                    withBorder
                    withColumnBorders
                    records={record.sizes}
                    columns={[
                      { accessor: 'Location', title: 'Location', width: 100 },
                      { accessor: 'Style', title: 'Style', width: 100 },
                      { accessor: 'Size', title: 'Size', width: 100 },
                      { accessor: 'StoreQty', title: 'Store Qty', width: 100 },
                      {
                        accessor: 'RequestQty',
                        title: 'Request Qty',
                        width: 200,
                        render: (sizeRecord) => (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <TextInput
                              placeholder="Qty"
                              value={sizeRequests[record.Style]?.[sizeRecord.Size]?.quantity || ''}
                              onChange={(e) => {
                                const inputValue = e.currentTarget.value;
                                // Allow only numeric input
                                if (!isNaN(Number(inputValue))) {
                                  handleSizeRequestChange(record.Style, sizeRecord.Size, 'quantity', Number(inputValue));
                                }
                              }}
                            />
                            <Textarea
                              placeholder="Notes"
                              value={sizeRequests[record.Style]?.[sizeRecord.Size]?.notes || ''}
                              onChange={(e) => handleSizeRequestChange(record.Style, sizeRecord.Size, 'notes', e.currentTarget.value)}
                              autosize
                              minRows={1}
                              maxRows={4}
                            />
                          </Box>
                        ),
                      },
                      {
                        accessor: 'Action',
                        title: 'Submit',
                        width: 100,
                        render: (sizeRecord) => (
                          <Button
                            variant="filled"
                            color="blue"
                            size="xs"
                            onClick={() => handleRequestSubmit(record.Style)}
                          >
                            Submit
                          </Button>
                        ),
                      },
                      { accessor: 'DCQty', title: 'DC Qty', width: 100 },
                      { accessor: 'dcLocation', title: 'DC_Location', width: 200 },
                    ]}
                    style={{ marginTop: 5 }}
                  />
                </Box>
              </Collapse>
            ),
          }}
          totalRecords={aggregatedData.length}
          paginationColor="grape"
          page={page}
          onPageChange={setPage}
          recordsPerPage={recordsPerPage}
          classNames={classes}
          sx={{
            th: {
              backgroundColor: theme.colors.yellow[2], // Header background color
              position: 'sticky',
              top: 0,
              zIndex: 1
            },
            'tbody tr:nth-of-type(odd)': {
              backgroundColor: theme.colors.gray[1],
              whiteSpace: 'nowrap' // Alternating row color
            },
            'tbody tr:nth-of-type(even)': {
              backgroundColor: theme.colors.dark[0],
              whiteSpace: 'nowrap'
            },

          }}

        />
      </Box>
      <Box className={classes.inputSection}>
        <Grid mt="xs">
          <Grid.Col xs={6} sm={1}>
            <MultiSelect
              label="Brand"
              data={brandsData}
              value={brand}
              onChange={setBrand}
              searchable
              className={classes.noBorder}
              placeholder="Select brands"
            />
          </Grid.Col>
          <Grid.Col xs={6} sm={1}>
            <MultiSelect
              label="Department"
              data={departmentsData}
              value={department}
              onChange={setDepartment}
              searchable
              className={classes.noBorder}
              placeholder="Select departments"
            />
          </Grid.Col>
          <Grid.Col xs={6} sm={1}>
            <MultiSelect
              label="Class"
              data={classesData}
              value={productClass}
              onChange={setProductClass}
              searchable
              className={classes.noBorder}
              placeholder="Select classes"
            />
          </Grid.Col>
          <Grid.Col xs={6} sm={1}>
            <Select
              label="DC Location"
              data={locationOptionsData}
              value={locationOptions}
              onChange={(value) => setLocationOptions(value || 'DC-Richmond')}
              className={classes.noBorder}
              placeholder="Select location"
            />
          </Grid.Col>
          <Grid.Col xs={6} sm={1}>
            <NumberInput
              label="QtyLess(Store)"
              value={qtyLess}
              onChange={(value) => setQtyLess(value || 3)}
              className={classes.noBorder}
            />
          </Grid.Col>
          <Grid.Col xs={6} sm={1}>
            <NumberInput
              label="QtyGreater(DC)"
              value={qtyGreater}
              onChange={(value) => setQtyGreater(value || 30)}
              className={classes.noBorder}
            />
          </Grid.Col>

          <Grid.Col xs={6} sm={5}>
          <TextInput
    label="Style Codes"
    placeholder="Enter style codes separated by commas"
    value={currentInput}
    onChange={handleStyleCodesChange}
    onBlur={handleCommitStyles} // Commit changes on blur
    onKeyDown={(e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleCommitStyles(); // Commit changes on Enter key
      }
    }}
    className={classes.noBorder}
  />
            <Box mt="xs">
              {separatedStyleCodes.map((code, index) => (
                <Badge key={index} color="blue" variant="outline" mr="xs">
                  {code}
                </Badge>
              ))}
            </Box>
          </Grid.Col>
          <Grid.Col xs={6} sm={1}>
            <Button onClick={fetchData} fullWidth variant="filled" color="blue" sx={{ marginTop: '23px' }} >
              GetData
            </Button>
          </Grid.Col>
        </Grid>


      </Box>
    </>
  );
};

export default InventoryTable;